// ✅ ClientDashboard.jsx (Ant Design 기반)
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import BasicLayout from '@/components/common/layout/basic';
import { Typography, Row, Col, Card, Table, Tag, Divider, message, Modal } from 'antd';
import { SwapLeftOutlined } from '@ant-design/icons';
import { Input, Button, Select, Checkbox } from 'antd';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Doughnut } from 'react-chartjs-2';
import { BarChartOutlined, TeamOutlined, CalendarOutlined, TableOutlined } from '@ant-design/icons';
import useAuthStore from '@store/useAuthStore';
import { useApi } from '@/hooks/useApi';
import clsx from 'clsx';
import api from '@/api/api';
import i18n from '@/i18n';
import dayjs from 'dayjs';
import {
  EstimateStatusCode,
  EstimateStatusCodeColor,
  EstimateStatusOrderIndex,
} from '@/constants/status';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const { Title } = Typography;
const CardHeaderStyle = {
  fontWeight: 'medium',
  color: '#737373',
  fontSize: '1rem',
  borderBottom: '1px solid #cccccc',
};

function ClientDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ParamsProjectCode = searchParams.get('project_code');

  const client_user = useAuthStore((state) => state.user);

  const [isPageLoaded, setPageLoaded] = useState(false);
  const [isProgressDownloading, setProgressDownloading] = useState(false);
  const [SelectedProject, setSelectedProject] = useState(null);
  const [ProjectDuration, setProjectDuration] = useState({
    start_date: null,
    end_date: null,
    target_end_date: null,
  });

  function generateProgressDataset() {
    return Object.entries(EstimateStatusCode).reduce((progressDataSet, [key, value]) => {
      progressDataSet[key] = {
        color: EstimateStatusCodeColor[key],
        count: 0,
        label: value,
      };
      return progressDataSet;
    }, {});
  }

  const CompanyProgressDataSet = generateProgressDataset();
  const SiteProgressDataSet = generateProgressDataset();
  const [ProjectStatic, setProjectStatic] = useState({
    TotalCompanies: 0,
    RequestedCompanies: 0,
    TotalSites: 0,
    companyProgress: { ...CompanyProgressDataSet },
    siteProgress: { ...CompanyProgressDataSet },
  });
  const [ProjectCompanies, setProjectCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);

  // 1. useApi로 프로젝트 목록 가져오기
  const { data: projectList, error: projectListError } = useApi(
    client_user ? `/clients/${client_user.company_id}/projects` : null
  );

  // 2. 프로젝트 목록 조회 실패 시 에러 메시지 표시
  useEffect(() => {
    if (projectListError) {
      message.error('프로젝트 목록을 불러오는 중 오류가 발생했습니다.');
    }
  }, [projectListError]);

  const updateSelectedProject = useCallback(async (project) => {
    setPageLoaded(false);
    updateDashboardURLProjectQeury(project);
    setSelectedProject(project);

    // 대시보드 데이터 초기화
    initDashboardProjectData();

    try {
      setTimeout(async () => {
        let ResProejct = await api.get(`/projects`, { params: { project_id: project.project_id } });
        ResProejct = ResProejct.data;

        // ProjectDuration
        setProjectDuration({
          end_date: !ResProejct.end_date
            ? ResProejct.end_date
            : dayjs(ResProejct.end_date).format('YYYY년 MM월 DD일'),
          start_date: !ResProejct.start_date
            ? ResProejct.start_date
            : dayjs(ResProejct.start_date).format('YYYY년 MM월 DD일'),
          target_end_date: !ResProejct.target_end_date
            ? ResProejct.target_end_date
            : dayjs(ResProejct.target_end_date).format('YYYY년 MM월 DD일'),
        });

        let ResProejctProgress = await api.get(`/projects/${project.project_id}/progress`);
        ResProejctProgress = ResProejctProgress.data;
        ResProejctProgress = ResProejctProgress.sort(
          (a, b) => b.used - a.used || b.sites.length - a.sites.length
        );
        if (ResProejctProgress.length) {
          const TotalCompanies = ResProejctProgress.length;
          const RequestedCompanies = ResProejctProgress.filter(({ used }) => used === true).length;

          const CompanyStatusCounts = ResProejctProgress.reduce(
            (_CompanyStatusSet, { estimate_info_status }) => {
              if (typeof _CompanyStatusSet[estimate_info_status] !== 'undefined') {
                _CompanyStatusSet[estimate_info_status]['count'] += 1;
              } else {
                _CompanyStatusSet['NOT_STARTED']['count'] += 1;
              }
              return _CompanyStatusSet;
            },
            JSON.parse(JSON.stringify(CompanyProgressDataSet))
          );

          const SiteStatusCounts = ResProejctProgress.reduce(
            (_SiteStatusSet, { sites }) => {
              if (sites.length) {
                sites.forEach(({ estimate_site_info_status }) => {
                  if (
                    estimate_site_info_status !== 'NOT_REGISTERED' &&
                    typeof _SiteStatusSet[estimate_site_info_status] !== 'undefined'
                  ) {
                    _SiteStatusSet[estimate_site_info_status]['count'] += 1;
                  }
                });
              }
              return _SiteStatusSet;
            },
            JSON.parse(JSON.stringify(SiteProgressDataSet))
          );

          const TotalSite = Object.values(SiteStatusCounts).reduce(
            (_TotalSite, { count }) => (_TotalSite += count),
            0
          );

          setProjectStatic({
            TotalCompanies,
            RequestedCompanies,
            TotalSites: TotalSite,
            companyProgress: CompanyStatusCounts,
            siteProgress: SiteStatusCounts,
          });

          setProjectCompanies(ResProejctProgress);
        } else {
          setProjectStatic({
            TotalCompanies: 0,
            RequestedCompanies: 0,
            TotalSites: 0,
            companyProgress: { ...CompanyProgressDataSet },
            siteProgress: { ...CompanyProgressDataSet },
          });

          setProjectCompanies([]);
        }
      }, 700);
    } catch (error) {
      console.error('Project data loading error:', error);
      message.error('프로젝트 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setPageLoaded(true);
    }
  }, []);

  useEffect(() => {
    setFilteredCompanies(ProjectCompanies);
  }, [ProjectCompanies]);

  // 3. useApi로 가져온 projectList를 기반으로 대시보드 업데이트 로직 실행
  useEffect(() => {
    if (!projectList) return;

    (async () => {
      try {
        if (projectList.length) {
          let _SelectProject = projectList[0];
          if (ParamsProjectCode) {
            _SelectProject = projectList.find(
              ({ project_code }) => project_code === ParamsProjectCode
            );
          }
          if (_SelectProject) {
            await updateSelectedProject(_SelectProject);
          }
        }
      } catch (error) {
        message.error(
          `${SelectedProject?.[`project_name_${i18n.language}`] || '프로젝트'} - 진행현황을 조회하는 도중 장애가 발생했습니다. 잠시 후 다시 시도해주세요.`
        );
      } finally {
        setPageLoaded(true);
      }
    })();
  }, [projectList, ParamsProjectCode, updateSelectedProject]);

  function updateDashboardURLProjectQeury(project) {
    // 1) 현재 URL 가져오기
    const url = new URL(window.location.href);
    // 2) 쿼리 파라미터 설정/추가
    url.searchParams.set('project_code', project.project_code);
    // 3) 히스토리 스택에 새 URL 반영 (페이지 리로드 없이 주소만 바뀜)
    window.history.pushState({}, '', url);
  }

  function initDashboardProjectData() {
    setProjectDuration({
      start_date: null,
      end_date: null,
      target_end_date: null,
    });

    setProjectStatic({
      TotalCompanies: 0,
      RequestedCompanies: 0,
      TotalSites: 0,
      companyProgress: { ...CompanyProgressDataSet },
      siteProgress: { ...CompanyProgressDataSet },
    });

    setProjectCompanies([]);
  }

  // Chart.js 데이터 구성
  const chartData = {
    labels: Object.keys(ProjectStatic.companyProgress).map((status) => EstimateStatusCode[status]),
    datasets: [
      {
        data: Object.values(ProjectStatic.companyProgress).map(({ count }) =>
          Number(((count / ProjectStatic.TotalCompanies) * 100).toFixed(1))
        ),
        backgroundColor: Object.values(ProjectStatic.companyProgress).map(({ color }) => color),
        borderWidth: 0,
        cutout: '50%',
        datalabels: {
          display: function (ctx) {
            return ctx['dataset']['data'][ctx.dataIndex] > 10; // 값이 0보다 클 때만 표시
          },
          formatter: (value) => {
            return value > 10 ? value + '%' : '';
          },
          color: '#fff',
          font: {
            weight: 'bold',
            size: 12,
          },
          anchor: 'center',
          align: 'center',
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: false,
      tooltip: false,
    },
  };

  const StatCard = ({ title, value, unit, icon, iconColor }) => (
    <Card className="shadow-sm">
      <div className="d-flex justify-content-between">
        <div className="fw-medium text-gray-7 fs-6">{title}</div>

        <div
          className={clsx('border border-3 rounded fs-5', iconColor)}
          style={{ padding: '0.25rem 0.65rem' }}
        >
          {icon}
        </div>
      </div>

      <div className="fw-medium fs-3">
        {value} {unit}
      </div>
    </Card>
  );

  const ProjectPeriodCard = ({ label, date, icon, iconColor }) => (
    <Card size="small">
      <div className="d-flex align-items-middle">
        <div
          className={clsx(
            'd-flex align-items-center justify-content-between border border-3 rounded fs-5 me-3',
            iconColor
          )}
          // iconColor
          style={{ padding: '0.25rem 0.65rem' }}
        >
          {icon}
        </div>
        <div className="">
          <div className="fw-medium text-gray-7 mb-0">{label}</div>
          <div className="fw-semibold fs-6">{!date ? ' - ' : date}</div>
        </div>
      </div>
    </Card>
  );

  async function downloadProjectProgressExcel() {
    try {
      setProgressDownloading(true);
      const response = await api.get(`/projects/${SelectedProject.project_id}/progress`, {
        params: { format: 'file' },
        responseType: 'blob',
      });

      let filename = `프로젝트 진행 현황.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (filenameMatch?.[1]) {
          filename = decodeURIComponent(filenameMatch[1]);
        } else {
          const fallbackMatch = contentDisposition.match(/filename="(.+)"/);
          if (fallbackMatch?.[1]) {
            filename = fallbackMatch[1];
          }
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error(
        '프로젝트 진행 현황 엑셀 다운로드 중 장애가 발생했습니다. 개발팀에 문의해주세요'
      );
    } finally {
      setProgressDownloading(false);
    }
  }

  const [filters, setFilters] = useState({
    company_name: '',
    site_name: '',
    vendor_code: '',
    used: null,
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    let filteredData = [...ProjectCompanies];

    if (filters.company_name) {
      filteredData = filteredData.filter((company) =>
        company.company_name.toLowerCase().includes(filters.company_name.toLowerCase())
      );
    }
    if (filters.vendor_code) {
      filteredData = filteredData.filter((company) =>
        company.vendor_code.toLowerCase().includes(filters.vendor_code.toLowerCase())
      );
    }
    if (filters.site_name) {
      filteredData = filteredData.filter((company) =>
        company.sites.some((site) =>
          site.site_name.toLowerCase().includes(filters.site_name.toLowerCase())
        )
      );
    }
    if (filters.used !== null) {
      filteredData = filteredData.filter((company) => company.used === filters.used);
    }

    setFilteredCompanies(filteredData);
  };

  const resetFilters = () => {
    setFilters({
      company_name: '',
      site_name: '',
      vendor_code: '',
      used: null,
    });
    setFilteredCompanies(ProjectCompanies);
  };

  const columns = [
    {
      title: '번호',
      dataIndex: 'index',
      key: 'index',
      className: 'text-center',
      render: (_, record, index) => {
        return ++index;
      },
    },
    {
      title: '참여기업명',
      dataIndex: 'company_name',
      key: 'company_name',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="기업명 입력 후 엔터를 쳐주세요."
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
        </div>
      ),
      onFilter: (value, record) => record.company_name.includes(value),
      sorter: (a, b) => a.company_id - b.company_id,
    },
    {
      title: '벤더코드',
      dataIndex: 'vendor_code',
      key: 'vendor_code',
      filterSearch: true,
      sorter: (a, b) => a.vendor_code - b.vendor_code,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="벤더코드 입력 후 엔터를 쳐주세요."
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
        </div>
      ),
      onFilter: (value, record) => record.vendor_code.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: '참여유무',
      dataIndex: 'used',
      key: 'used',
      align: 'center',
      sorter: (a, b) => (a.used === b.used ? 0 : a.used ? 1 : -1),
      onFilter: (value, record) => record.used === value,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="d-flex flex-column">
          <div className="p-2">
            <Checkbox.Group
              className="d-flex flex-column"
              options={[
                { label: '참여중', value: true },
                { label: '미신청', value: false },
              ]}
              value={selectedKeys}
              onChange={(vals) => setSelectedKeys(vals)}
            />
          </div>
          <Divider className="my-1" />
          <div className="d-flex justify-content-between p-2">
            <Button size="small" onClick={() => clearFilters()}>
              Reset
            </Button>
            <Button type="primary" size="small" className="" onClick={() => confirm()}>
              OK
            </Button>
          </div>
        </div>
      ),
      render: (_, { used }) => {
        return (
          <>
            <span
              className={clsx(
                used ? 'bg-blue-7' : 'bg-gray-7',
                'py-1',
                'px-2',
                'rounded-pill',
                'text-white',
                'fs-7'
              )}
              style={{ fontSize: '11px' }}
            >
              {used ? '참여중' : '미신청'}
            </span>
          </>
        );
      },
    },
    {
      title: '상태',
      dataIndex: 'estimate_info_status',
      key: 'estimate_info_status',
      align: 'center',
      sorter: (a, b) =>
        EstimateStatusOrderIndex[a.estimate_info_status] -
        EstimateStatusOrderIndex[b.estimate_info_status],
      onFilter: (value, record) => value.includes(record.estimate_info_status),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="d-flex flex-column">
          <div className="p-2">
            <Checkbox.Group
              className="d-flex flex-column"
              options={Object.entries(EstimateStatusCode).map(([status_code, label]) => ({
                label: label,
                value: status_code,
              }))}
              value={selectedKeys}
              onChange={(vals) => setSelectedKeys(vals)}
            />
          </div>
          <Divider className="my-1" />
          <div className="d-flex justify-content-between p-2">
            <Button size="small" onClick={() => clearFilters()}>
              Reset
            </Button>
            <Button type="primary" size="small" className="" onClick={() => confirm()}>
              OK
            </Button>
          </div>
        </div>
      ),
      render: (_, { estimate_info_status }) => {
        return (
          <>
            <Tag
              className={clsx('py-1', 'px-2', 'rounded-pill', 'text-white', 'fs-7')}
              color={EstimateStatusCodeColor[estimate_info_status]}
            >
              {EstimateStatusCode[estimate_info_status]}
            </Tag>
          </>
        );
      },
    },
    {
      title: '사업장 수',
      dataIndex: 'sites',
      key: 'sites',
      align: 'center',
      sorter: (a, b) => a.sites.length - b.sites.length,
      render: (_, { sites }) => {
        return <>{sites.length} 개</>;
      },
    },

    {
      title: '자세히 보기',
      dataIndex: 'cta',
      key: 'cta',
      align: 'center',
      render: (_, estimate) => {
        return (
          <>
            <Button
              type="primary"
              onClick={() =>
                navigate(
                  `/client/dashboard/${SelectedProject.project_id}/estimates/${estimate.estimate_info_id}?project_code=${SelectedProject.project_code}`
                )
              }
              disabled={!estimate.used}
            >
              {' '}
              자세히 보기{' '}
            </Button>
          </>
        );
      },
    },
  ];

  return (
    <BasicLayout body_classname="bg-light">
      <Modal open={!isPageLoaded} footer={null} centered>
        <div className="text-center">
          <div>프로젝트 진행현황을 조회중입니다.</div>
          <div>잠시만 기다려주세요.</div>
        </div>
      </Modal>
      {isPageLoaded && (
        <>
          <Row gutter={[24, 24]} className="mb-5">
            <Col xs={24} lg={{ span: 16, offset: 2 }}>
              <div className="text-start mb-1">
                <Link className="text-gray-7 hover-text-blue-8 fs-6" to={`/projects`}>
                  <SwapLeftOutlined className="me-2" />
                  메인 페이지로 이동
                </Link>
              </div>
              <Typography.Title level={1}>
                {SelectedProject[`project_name_${i18n.language}`]}
              </Typography.Title>
            </Col>
            <Col xs={24} lg={4}>
              <div className="text-end">
                <p className="text-end text-gray-6 mb-1">프로젝트 선택</p>
                {projectList && projectList.length > 0 && (
                  <Select
                    value={SelectedProject.project_id}
                    options={projectList}
                    fieldNames={{
                      label: `project_code`,
                      value: 'project_id',
                    }}
                    onChange={(_, option) => updateSelectedProject(option)}
                  />
                )}
              </div>
            </Col>
          </Row>
        </>
      )}

      <Row gutter={[24, 24]} align={'bottom'} className="mb-3">
        {/* 헤더 */}
        <Col xs={24} lg={{ span: 12, offset: 2 }}>
          <Title level={2} className="text-gray-8 mb-0">
            진행 현황
          </Title>
        </Col>
        <Col xs={24} lg={8}>
          <div className="text-gray-6 text-end">
            최근 조회 시간: {dayjs().format('YYYY년 M월 DD일 A HH:MM:ss')}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mb-5">
        <Col xs={24} md={12} lg={{ span: 6, offset: 2 }}>
          <div className="d-flex flex-column justify-content-between h-100">
            <StatCard
              title="전체 기업"
              value={ProjectStatic.TotalCompanies}
              unit="개 기업"
              icon={<TeamOutlined />}
              iconColor={'border-gray-7 bg-gray-2 text-gray-8'}
            />
            <StatCard
              title="현재 참여 기업"
              value={ProjectStatic.RequestedCompanies}
              unit="개 기업"
              icon={<BarChartOutlined />}
              iconColor={'border-blue-6 bg-blue-1 text-blue-7'}
            />
            <StatCard
              title="현재 참여 사업장"
              value={ProjectStatic.TotalSites}
              unit="개 사업장"
              icon={<BarChartOutlined />}
              iconColor={'border-green-6 bg-green-1 text-green-7'}
            />
          </div>
        </Col>

        {/* 차트 및 상세 현황 */}
        <Col xs={24} lg={14}>
          <Card
            title="총 참가기업 현황"
            headStyle={CardHeaderStyle}
            className="d-flex flex-column shadow-sm h-100"
            bodyStyle={{ flexGrow: 1 }}
          >
            <Row gutter={[24, 24]} className="h-100">
              <Col xs={12} lg={{ span: 10, offset: 1 }}>
                <div className="d-flex flex-column h-100">
                  <div className="flex-grow-1 d-flex align-items-center">
                    <div
                      style={{
                        height: '85%',
                        position: 'relative',
                      }}
                    >
                      <Doughnut data={chartData} options={chartOptions} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <Row gutter={[8, 8]}>
                      {Object.entries(CompanyProgressDataSet).map(([key, config], index) => (
                        <Col xs={12} key={`project-company-stats-chart-legend-${key}-${index}`}>
                          <div className="d-flex align-items-center">
                            <div
                              className="me-2"
                              style={{
                                backgroundColor: config.color,
                                width: '25px',
                                height: '12px',
                                borderRadius: '2px',
                              }}
                            />
                            <div style={{ fontSize: '0.9rem' }}>{config.label}</div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={6}>
                <div className="h-100">
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="me-2"
                      style={{
                        backgroundColor: '#1890ff',
                        width: '4px',
                        height: '20px',
                        borderRadius: '2px',
                      }}
                    />
                    <Typography.Title level={4} className="mb-0">
                      기업현황
                    </Typography.Title>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {Object.entries(ProjectStatic.companyProgress).map(([key, config], index) => (
                      <div
                        className="d-flex justify-content-between align-items-center bg-light rounded p-3 border-start border-3"
                        style={{ borderLeftColor: config.color }}
                        key={`project-company-stats-${key}-${index}`}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="me-2"
                            style={{
                              backgroundColor: config.color,
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                            }}
                          />
                          <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                            {config.label}
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            className="fw-bold text-primary me-1"
                            style={{ fontSize: '0.9rem' }}
                          >
                            {config.count}
                          </span>
                          <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                            개
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={6}>
                <div className="h-100">
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="me-2"
                      style={{
                        backgroundColor: '#52c41a',
                        width: '4px',
                        height: '20px',
                        borderRadius: '2px',
                      }}
                    />
                    <Typography.Title level={4} className="mb-0">
                      사업장 현황
                    </Typography.Title>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {Object.entries(ProjectStatic.siteProgress).map(([key, config], index) => (
                      <div
                        className="d-flex justify-content-between align-items-center bg-light rounded p-3 border-start border-3"
                        style={{ borderLeftColor: config.color }}
                        key={`project-site-stats-${key}-${index}`}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="me-2"
                            style={{
                              backgroundColor: config.color,
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                            }}
                          />
                          <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                            {config.label}
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            className="fw-bold text-success me-1"
                            style={{ fontSize: '0.9rem' }}
                          >
                            {config.count}
                          </span>
                          <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                            개
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mb-5">
        {/* 프로젝트 기간 */}
        <Col xs={24} lg={{ span: 20, offset: 2 }}>
          <Card
            title="프로젝트 기간"
            className="shadow-sm"
            headStyle={CardHeaderStyle}
            extra={<CalendarOutlined className="fs-5" />}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <ProjectPeriodCard
                  label="프로젝트 시작일"
                  date={ProjectDuration.start_date}
                  icon={<CalendarOutlined />}
                  iconColor={'border-green-6 bg-green-1 text-green-7'}
                />
              </Col>
              <Col xs={24} md={8}>
                <ProjectPeriodCard
                  label="목표 종료일"
                  date={ProjectDuration.target_end_date}
                  icon={<CalendarOutlined />}
                  iconColor={'border-gray-6 bg-gray-1 text-gray-7'}
                />
              </Col>
              <Col xs={24} md={8}>
                <ProjectPeriodCard
                  label="프로젝트 종료일"
                  date={ProjectDuration.end_date}
                  icon={<CalendarOutlined />}
                  iconColor={'border-purple-6 bg-purple-1 text-purple-7'}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* 프로젝트 기간 */}
        <Col xs={24} lg={{ span: 20, offset: 2 }}>
          <Card
            title="기업별 진행현황"
            className="shadow-sm"
            headStyle={{ ...CardHeaderStyle, marginBottom: '0px' }}
            bodyStyle={{ padding: '24px' }}
            extra={
              <div className="d-flex align-items-center">
                <Button className="me-2" onClick={() => downloadProjectProgressExcel()}>
                  프로젝트 진행현황 다운로드
                </Button>
                <TableOutlined className="fs-5" />
              </div>
            }
          >
            <div
              style={{
                marginBottom: 16,
                padding: 24,
                backgroundColor: '#fbfbfb',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
              }}
            >
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder="기업명"
                    value={filters.company_name}
                    onChange={(e) => handleFilterChange('company_name', e.target.value)}
                  />
                </Col>
                <Col span={6}>
                  <Input
                    placeholder="사업장명"
                    value={filters.site_name}
                    onChange={(e) => handleFilterChange('site_name', e.target.value)}
                  />
                </Col>
                <Col span={6}>
                  <Input
                    placeholder="벤더코드"
                    value={filters.vendor_code}
                    onChange={(e) => handleFilterChange('vendor_code', e.target.value)}
                  />
                </Col>
                <Col span={6}>
                  <Select
                    allowClear
                    placeholder="참여유무"
                    style={{ width: '100%' }}
                    value={filters.used}
                    onChange={(value) => handleFilterChange('used', value)}
                  >
                    <Select.Option value={true}>참여중</Select.Option>
                    <Select.Option value={false}>미참여중</Select.Option>
                  </Select>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ textAlign: 'right', marginTop: 16 }}>
                  <Button onClick={resetFilters} style={{ marginRight: 8 }}>
                    초기화
                  </Button>
                  <Button type="primary" onClick={applyFilters}>
                    검색
                  </Button>
                </Col>
              </Row>
            </div>
            <Table
              dataSource={filteredCompanies}
              columns={columns}
              pagination={{
                position: ['bottomCenter'],
              }}
              locale={{
                emptyText: (
                  <div className="fs-6 fw-medium p-5">
                    조회된 프로젝드 대상 참여 기업이 없습니다.
                  </div>
                ),
              }}
            />
          </Card>
        </Col>
      </Row>
    </BasicLayout>
  );
}

export default ClientDashboard;
